import { useRef, useState } from "react";
import { Button, useWindowSize } from "@newsela/angelou";

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
  withTrackEvent,
} from "@newsela/snitch";

import AISearchWithSparkleIcon from "@/icons/AISearchWithSparkleIcon/AISearchWithSparkleIcon";

import { NAVBAR_BREAKPOINTS } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";
import AIExpandableTextArea from "../AIExpandableTextArea";

import {
  buttonWithPopOutRootCss,
  buttonIconContainerCss,
  buttonIconBorderCss,
  searchToggleButtonSvgCss,
  popOutCss,
} from "./AISearchMenuContainer.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

const AISearchMenuContainer = () => {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { width: windowWidth } = useWindowSize();
  const isMobileDisplay = windowWidth < NAVBAR_BREAKPOINTS.sm;

  const { trackAction, getEventName } = useTracker();

  const handleContainerBlur = (e: React.FocusEvent) => {
    // Ensures that tabbing into elements within container
    // doesn't collapse the container. Only collapses when
    // focus is set outside of container.
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setExpanded(false);
    }
  };

  const handleExpandAndFocus = () => {
    setExpanded(true);

    trackAction({
      event_type: eventTypes.click,
      event_name: getEventName(
        objectTypes.user,
        actionPrefixes.initiate,
        actionNames.interact,
      ),
      dimension_properties: {
        description: "Toggle open the Search with Luna input.",
      },
    });
  };

  const handleToggleSearch = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      {isMobileDisplay ? (
        <li>
          <ButtonWithTrackEvent
            flavor={Button.flavors.incognito}
            size={Button.sizes.sm}
            onClick={handleToggleSearch}
            onBlur={handleContainerBlur}
            __cssFor={{
              root: buttonWithPopOutRootCss,
            }}
            objectType={objectTypes.user}
            actionName={actionNames.interact}
            streamProperties={{
              description: "Toggle Search with Luna search bar on mobile.",
            }}
            dataProps={{
              "data-qa-selector": "search-with-luna-mobile-toggle-button",
            }}
          >
            <div css={buttonIconContainerCss}>
              <div css={buttonIconBorderCss}>
                <AISearchWithSparkleIcon css={searchToggleButtonSvgCss} />
              </div>
            </div>
          </ButtonWithTrackEvent>
          {expanded ? (
            <div css={popOutCss}>
              <AIExpandableTextArea
                containerRef={containerRef}
                expanded={expanded}
                handleContainerBlur={handleContainerBlur}
                handleExpandAndFocus={handleExpandAndFocus}
                inputValue={inputValue}
                isMobileDisplay
                setExpanded={setExpanded}
                setInputValue={setInputValue}
                textAreaRef={textAreaRef}
              />
            </div>
          ) : null}
        </li>
      ) : (
        <li>
          <AIExpandableTextArea
            containerRef={containerRef}
            expanded={expanded}
            handleExpandAndFocus={handleExpandAndFocus}
            handleContainerBlur={handleContainerBlur}
            inputValue={inputValue}
            setExpanded={setExpanded}
            setInputValue={setInputValue}
            textAreaRef={textAreaRef}
          />
        </li>
      )}
    </>
  );
};

export default AISearchMenuContainer;
